import { Router, type IRouter } from "express";
import Stripe from "stripe";
import { db, sellerLeadsTable, usersTable, paymentsTable } from "@workspace/db";
import { eq, and } from "drizzle-orm";
import { CreateSellerLeadBody, UpdateSellerLeadBody, SendSellerPaymentLinkBody } from "@workspace/api-zod";
import { requireAuth, requireRole } from "../middlewares/auth";
import { logger } from "../lib/logger";

const router: IRouter = Router();

const STRIPE_SECRET_KEY = process.env.STRIPE_SECRET_KEY;
const stripe = STRIPE_SECRET_KEY ? new Stripe(STRIPE_SECRET_KEY) : null;

const SELLER_PLAN_PRICES: Record<string, { amount: number; name: string }> = {
  urgent_seller: { amount: 500000, name: "Urgent Seller Campaign — AED 5,000" },
  concierge_sale: { amount: 1500000, name: "Private Concierge Sale — AED 15,000" },
};

const BANK_DETAILS = `Bank: Emirates NBD\nAccount Name: VaultDXB Properties LLC\nIBAN: AE070260001234567890123\nSwift: EBILAEAD\nReference: SELLER-{ID}`;

function toPublicAgent(agent: typeof usersTable.$inferSelect) {
  return {
    id: agent.id,
    name: agent.name,
    email: agent.email,
    phone: agent.phone,
    role: agent.role,
    membership: agent.membership,
    status: agent.status,
    paymentStatus: agent.paymentStatus,
    stripeCustomerId: agent.stripeCustomerId,
    createdAt: agent.createdAt,
  };
}

function formatLead(
  lead: typeof sellerLeadsTable.$inferSelect,
  assignedAgent?: typeof usersTable.$inferSelect | null
) {
  return {
    id: lead.id,
    name: lead.name,
    phone: lead.phone,
    email: lead.email,
    propertyArea: lead.propertyArea,
    buildingProject: lead.buildingProject,
    propertyType: lead.propertyType,
    bedrooms: lead.bedrooms,
    askingPrice: parseFloat(lead.askingPrice),
    mortgageOutstanding: lead.mortgageOutstanding ? parseFloat(lead.mortgageOutstanding) : null,
    urgencyReason: lead.urgencyReason,
    timelineToSell: lead.timelineToSell,
    additionalNotes: lead.additionalNotes,
    photoUrls: lead.photoUrls,
    status: lead.status,
    paymentStatus: lead.paymentStatus,
    paymentPlan: lead.paymentPlan,
    assignedAgentId: lead.assignedAgentId,
    adminNotes: lead.adminNotes,
    agentNotes: lead.agentNotes,
    aiLeadTemperature: lead.aiLeadTemperature ?? null,
    aiUrgencyScore: lead.aiUrgencyScore ?? null,
    aiLikelyMotivation: lead.aiLikelyMotivation ?? null,
    aiSuggestedAgentScript: lead.aiSuggestedAgentScript ?? null,
    aiRecommendedNextStep: lead.aiRecommendedNextStep ?? null,
    createdAt: lead.createdAt,
    updatedAt: lead.updatedAt,
    assignedAgent: assignedAgent ? toPublicAgent(assignedAgent) : null,
  };
}

router.post("/seller-leads", async (req, res): Promise<void> => {
  const parsed = CreateSellerLeadBody.safeParse(req.body);
  if (!parsed.success) {
    res.status(400).json({ error: parsed.error.message });
    return;
  }

  const data = parsed.data;
  const [lead] = await db
    .insert(sellerLeadsTable)
    .values({
      name: data.name,
      phone: data.phone,
      email: data.email,
      propertyArea: data.propertyArea,
      buildingProject: data.buildingProject ?? null,
      propertyType: data.propertyType,
      bedrooms: data.bedrooms ?? null,
      askingPrice: String(data.askingPrice),
      mortgageOutstanding: data.mortgageOutstanding != null ? String(data.mortgageOutstanding) : null,
      urgencyReason: data.urgencyReason,
      timelineToSell: data.timelineToSell,
      additionalNotes: data.additionalNotes ?? null,
      photoUrls: data.photoUrls ?? null,
      status: "new",
    })
    .returning();

  res.status(201).json(formatLead(lead, null));
});

router.get(
  "/seller-leads",
  requireAuth,
  requireRole("admin", "broker"),
  async (req, res): Promise<void> => {
    const { auth } = req;
    const { status } = req.query as Record<string, string | undefined>;

    const conditions = [];

    if (auth!.role === "broker") {
      conditions.push(eq(sellerLeadsTable.assignedAgentId, auth!.userId));
    } else if (status) {
      conditions.push(
        eq(
          sellerLeadsTable.status,
          status as "new" | "contacted" | "valuation" | "assigned" | "active" | "closed" | "rejected"
        )
      );
    }

    const query = db
      .select({ lead: sellerLeadsTable, agent: usersTable })
      .from(sellerLeadsTable)
      .leftJoin(usersTable, eq(sellerLeadsTable.assignedAgentId, usersTable.id));

    const rows = conditions.length > 0 ? await query.where(and(...conditions)) : await query;

    res.json(rows.map((r) => formatLead(r.lead, r.agent)));
  }
);

router.get(
  "/seller-leads/:id",
  requireAuth,
  requireRole("admin", "broker"),
  async (req, res): Promise<void> => {
    const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
    const id = parseInt(raw, 10);
    if (isNaN(id)) {
      res.status(400).json({ error: "Invalid id" });
      return;
    }

    const [row] = await db
      .select({ lead: sellerLeadsTable, agent: usersTable })
      .from(sellerLeadsTable)
      .leftJoin(usersTable, eq(sellerLeadsTable.assignedAgentId, usersTable.id))
      .where(eq(sellerLeadsTable.id, id));

    if (!row) {
      res.status(404).json({ error: "Seller lead not found" });
      return;
    }

    if (req.auth!.role === "broker" && row.lead.assignedAgentId !== req.auth!.userId) {
      res.status(403).json({ error: "Forbidden" });
      return;
    }

    res.json(formatLead(row.lead, row.agent));
  }
);

router.put(
  "/seller-leads/:id",
  requireAuth,
  requireRole("admin", "broker"),
  async (req, res): Promise<void> => {
    const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
    const id = parseInt(raw, 10);
    if (isNaN(id)) {
      res.status(400).json({ error: "Invalid id" });
      return;
    }

    const parsed = UpdateSellerLeadBody.safeParse(req.body);
    if (!parsed.success) {
      res.status(400).json({ error: parsed.error.message });
      return;
    }

    const data = parsed.data;
    const updateData: Record<string, unknown> = { updatedAt: new Date() };

    if (data.status !== undefined) updateData.status = data.status;
    if (data.adminNotes !== undefined) updateData.adminNotes = data.adminNotes;
    if (data.agentNotes !== undefined) updateData.agentNotes = data.agentNotes;
    if (data.paymentStatus !== undefined) updateData.paymentStatus = data.paymentStatus;
    if (data.paymentPlan !== undefined) updateData.paymentPlan = data.paymentPlan;

    if (req.auth!.role === "admin" && "assignedAgentId" in data) {
      updateData.assignedAgentId = data.assignedAgentId ?? null;
    }

    const [lead] = await db
      .update(sellerLeadsTable)
      .set(updateData)
      .where(eq(sellerLeadsTable.id, id))
      .returning();

    if (!lead) {
      res.status(404).json({ error: "Seller lead not found" });
      return;
    }

    const [agent] = lead.assignedAgentId
      ? await db.select().from(usersTable).where(eq(usersTable.id, lead.assignedAgentId))
      : [];

    res.json(formatLead(lead, agent));
  }
);

// Admin: send payment link to a seller lead
router.post(
  "/seller-leads/:id/send-payment-link",
  requireAuth,
  requireRole("admin"),
  async (req, res): Promise<void> => {
    const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
    const id = parseInt(raw, 10);
    if (isNaN(id)) {
      res.status(400).json({ error: "Invalid id" });
      return;
    }

    const parsed = SendSellerPaymentLinkBody.safeParse(req.body);
    if (!parsed.success) {
      res.status(400).json({ error: parsed.error.message });
      return;
    }

    const { plan, transferType } = parsed.data;
    const planInfo = SELLER_PLAN_PRICES[plan];
    if (!planInfo) {
      res.status(400).json({ error: "Invalid plan" });
      return;
    }

    // Mark the lead with the chosen plan
    await db
      .update(sellerLeadsTable)
      .set({
        paymentPlan: plan as "urgent_seller" | "concierge_sale",
        paymentStatus: "pending",
        updatedAt: new Date(),
      })
      .where(eq(sellerLeadsTable.id, id));

    if (transferType === "bank_transfer") {
      const bankDetails = BANK_DETAILS.replace("{ID}", String(id));
      res.json({
        url: null,
        bankDetails,
        plan,
        amount: planInfo.amount / 100,
      });
      return;
    }

    // Stripe checkout for seller
    if (!stripe) {
      logger.warn("Stripe not configured — mock seller payment link");
      await db.insert(paymentsTable).values({
        stripeSessionId: `mock_seller_${plan}_${id}_${Date.now()}`,
        plan: plan as "urgent_seller" | "concierge_sale",
        amount: String(planInfo.amount / 100),
        status: "pending",
      });
      res.json({ url: null, bankDetails: null, plan, amount: planInfo.amount / 100 });
      return;
    }

    // Get seller email from lead
    const [lead] = await db.select().from(sellerLeadsTable).where(eq(sellerLeadsTable.id, id));
    const session = await stripe.checkout.sessions.create({
      payment_method_types: ["card"],
      mode: "payment",
      customer_email: lead?.email,
      line_items: [
        {
          price_data: {
            currency: "aed",
            product_data: { name: planInfo.name },
            unit_amount: planInfo.amount,
          },
          quantity: 1,
        },
      ],
      success_url: `${req.headers.origin}/`,
      cancel_url: `${req.headers.origin}/`,
      metadata: { sellerLeadId: String(id), plan },
    });

    await db.insert(paymentsTable).values({
      stripeSessionId: session.id,
      plan: plan as "urgent_seller" | "concierge_sale",
      amount: String(planInfo.amount / 100),
      status: "pending",
    });

    res.json({ url: session.url, bankDetails: null, plan, amount: planInfo.amount / 100 });
  }
);

export default router;
